import React from 'react';
import { motion } from 'framer-motion';
import { IGroupData, IAccountDataResponse } from '@tool-no-hu/shared/types';
import { AccountSide } from '@tool-no-hu/shared/enums';
import { AccountCard } from './AccountCard';
import { GroupLivestreamModal } from './GroupLivestreamModal';
import { useSharedSession } from '../hooks/useSharedSession';
import { formatJackpot, type SessionState } from '../hooks/useLivestreamSession';
import { showToast } from '../context/ToastContext';
import { parseGameType } from '../utils/game';
import { summarizeGroupAction } from '../utils/group-result';
import { groupEligibility } from '../utils/group-eligibility';
import { getGroupByIdService } from '../service/group';
import {
  sunwinLoginGroupService,
  sunwinStartLivestreamService,
  sunwinStopLivestreamService,
  sunwinRestartLivestreamService,
  sunwinLogoutGroupService,
} from '../service/sunwin';
import { LogIn, LogOut, Monitor, Play, Square, RotateCw, Pencil } from 'lucide-react';

// ─── Types ──────────────────────────────────────────────────────────

interface GroupCardProps {
  group: IGroupData;
  accounts: IAccountDataResponse[];
  onUpdateAccount: (
    id: string,
    updater: (acc: IAccountDataResponse) => IAccountDataResponse,
  ) => void;
  onRefresh: () => Promise<void>;
  onUpdateGroup: (groupId: string, updater: (group: IGroupData) => IGroupData) => void;
  onDeleteAccount: (id: string) => void;
  onGetBalance: (id: string) => void;
  onGetUSDTQR: (id: string) => void;
  onGetHistory: (id: string) => void;
  onGetBetLogs: (id: string) => void;
  onWithdraw: (id: string) => void;
  /** Mở modal sửa nhóm — gộp tên nhóm, game và cấu hình của mọi thành viên. */
  onEditGroup?: (group: IGroupData) => void;
}

// ─── Helpers ────────────────────────────────────────────────────────

/** Số tiền cược cấu hình cho 1 side — 0 nếu group chưa cấu hình side đó. */
function getSideAmount(sideConfigs: IGroupData['sideConfigs'] | undefined, side: AccountSide): number {
  return sideConfigs?.find((s) => s.side === side)?.amount || 0;
}

function gameName(typegame: string): string {
  if (typegame === 'TX') return 'Tài Xỉu';
  if (typegame === 'XD') return 'Xóc Đĩa';
  if (typegame === 'TL') return 'Tứ Linh';
  return typegame;
}

const GAME_BADGE_COLORS: Record<string, string> = {
  TX: 'bg-[#3b82f6]/20 text-[#3b82f6]',
  XD: 'bg-[#8b5cf6]/20 text-[#8b5cf6]',
  TL: 'bg-[#f59e0b]/20 text-[#f59e0b]',
};

// ─── Shared session panel ───────────────────────────────────────────

interface SharedSessionPanelProps {
  session: SessionState;
  typegame: string;
}

const SharedSessionPanel: React.FC<SharedSessionPanelProps> = ({ session, typegame }) => {
  const gName = gameName(typegame);

  const countdownColor =
    session.status === 'WAITING'
      ? 'text-[#ef4444]'
      : session.countdown > 3
        ? 'text-[#22c55e]'
        : session.countdown > 0
          ? 'text-[#f59e0b]'
          : 'text-[#64748b]';

  const sessionStatusColor =
    session.status === 'BETTING'
      ? 'text-[#22c55e]'
      : session.status === 'WAITING'
        ? 'text-[#f59e0b]'
        : session.status === 'ROLLING'
          ? 'text-[#8b5cf6]'
          : session.status === 'ENDED'
            ? 'text-[#64748b]'
            : 'text-[#64748b]';

  return (
    <div className="rounded-lg border border-[#252a38] bg-[#161920] p-3">
      <div className="mb-2 flex items-center justify-between">
        <span className="text-[10px] font-bold uppercase tracking-wider text-[#64748b]">
          Thông tin phiên — {gName} ({typegame})
        </span>
        {session.dealerName && (
          <span className="text-[10px] text-[#64748b]">
            Dealer: <span className="font-bold text-[#22c55e]">{session.dealerName}</span>
          </span>
        )}
      </div>
      <div className="grid grid-cols-4 gap-3">
        <div>
          <div className="text-[9px] font-bold uppercase tracking-wider text-[#64748b]">Phiên</div>
          <div className="mt-0.5 text-sm font-extrabold text-[#3b82f6]">
            {session.sessionId ? `#${session.sessionId}` : '---'}
          </div>
        </div>
        <div>
          <div className="text-[9px] font-bold uppercase tracking-wider text-[#64748b]">
            Đặt phiên
          </div>
          <div className={`mt-0.5 text-sm font-extrabold ${countdownColor}`}>
            {session.countdown > 0 ? `${session.countdown}s` : session.sessionId ? 'Chờ...' : '---'}
          </div>
        </div>
        <div>
          <div className="text-[9px] font-bold uppercase tracking-wider text-[#64748b]">
            Trạng thái
          </div>
          <div className={`mt-0.5 text-[11px] font-extrabold ${sessionStatusColor}`}>
            {session.status || '---'}
          </div>
        </div>
        <div>
          <div className="text-[9px] font-bold uppercase tracking-wider text-[#64748b]">Hũ</div>
          <div className="mt-0.5 text-sm font-extrabold text-[#f59e0b]">
            {session.jackpot > 0 ? formatJackpot(session.jackpot) : '---'}
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── GroupCard ──────────────────────────────────────────────────────

export const GroupCard: React.FC<GroupCardProps> = ({
  group,
  accounts,
  onUpdateAccount,
  onRefresh,
  onUpdateGroup,
  onDeleteAccount,
  onGetBalance,
  onGetUSDTQR,
  onGetHistory,
  onGetBetLogs,
  onWithdraw,
  onEditGroup,
}) => {
  const [showPhiênModal, setShowPhiênModal] = React.useState(false);
  const [starting, setStarting] = React.useState(false);
  const [authAction, setAuthAction] = React.useState<'login' | 'logout' | null>(null);

  const memberAccounts = ((group.members || []) as IAccountDataResponse[]).filter(
    (m): m is IAccountDataResponse => typeof m !== 'string',
  );

  const memberAccountsMerged = memberAccounts.map((m) => {
    const fromState = accounts.find((a) => a._id === m._id || a.username === m.username);
    // accounts state là source of truth cho balance/token/profit..., nhưng config của member trong group
    // phải ưu tiên giữ từ group.members vì account document không còn là source of truth cho group config.
    return fromState ? { ...fromState, ...m } : m;
  });

  const memberColors = ['#60a5fa', '#34d399', '#a78bfa', '#fb923c', '#f472b6', '#2dd4bf'];

  const isGroupDisabled =
    memberAccountsMerged.length === 0 || memberAccountsMerged.some((m) => !m.accessToken);

  const isAllLoggedIn =
    memberAccountsMerged.length > 0 && memberAccountsMerged.every((m) => !!m.accessToken);

  const anyRunning = memberAccountsMerged.some((m) => m.status === 'RUNNING');

  // Luật `start` CHUNG với server (`classifyGroupAction`) — bắt buộc đúng 4 tài
  // khoản, khớp view "Tóm tắt" (GroupSummaryCard/bulk) vốn đã dùng hàm này. Thiếu
  // check này thì nút Start ở view "Chi tiết" chỉ tắt khi group rỗng/thiếu token,
  // vẫn bấm được với nhóm 1-3 (hoặc 5+) thành viên dù server giờ đã chặn.
  const startEligibility = groupEligibility(group, accounts).start;

  // Lấy game type từ group (ưu tiên) hoặc account đầu tiên để hiển thị badge
  const firstConfig = memberAccountsMerged[0]?.config;
  const gameType = group.gameType || parseGameType(firstConfig);
  const badgeColor = GAME_BADGE_COLORS[gameType] || 'bg-[#3b82f6]/20 text-[#3b82f6]';

  const sessionAccount =
    memberAccountsMerged.find((m) => m.status === 'RUNNING') ?? memberAccountsMerged[0];
  // Subscribe theo gameType, không theo account: session data trên server vốn
  // dùng chung cho cả game nên mọi group cùng game chia sẻ một connection.
  const { session: sharedSession } = useSharedSession(gameType, gameType !== '?' && anyRunning);

  // ─── Group actions ──────────────────────────────────────────────

  /**
   * Cập nhật status của 1 member ở CẢ accounts state lẫn group.members.
   * memberAccountsMerged spread `...m` (group.members) sau `...fromState`, nên nếu chỉ
   * update accounts state thì status cũ trong group.members vẫn thắng → UI không đổi
   * cho tới lần refetch group tiếp theo (phải F5). Lobby cũng lọc nhóm đang chạy bằng
   * group.members[].status nên phải patch cùng chỗ.
   */
  const setMemberStatus = (accountId: string, status: 'RUNNING' | 'STOPPED') => {
    onUpdateAccount(accountId, (a) => ({ ...a, status }));
    onUpdateGroup(group._id, (g) => ({
      ...g,
      members: ((g.members || []) as any[]).map((m: any) =>
        m &&
        typeof m !== 'string' &&
        (String(m._id) === accountId || String(m.accountId) === accountId)
          ? { ...m, status }
          : m,
      ),
    }));
  };

  /**
   * Báo kết quả thao tác nhóm ra toast.
   *
   * Backend trả code 200 kèm mảng kết quả TỪNG account (mỗi phần tử có lý do
   * lỗi riêng), kể cả khi mọi account đều hỏng. UI cũ chỉ đọc `res.code` nên
   * mọi lỗi cấp tài khoản đều biến mất — bấm Đăng nhập thấy không có gì xảy ra
   * và không biết vì sao.
   */
  const reportResult = (results: any, action: string) => {
    const summary = summarizeGroupAction(results, action, (accountId) =>
      memberAccountsMerged.find((m) => String(m._id) === accountId)?.username,
    );
    if (summary.failCount === 0) {
      showToast.success(summary.message);
    } else {
      // Lỗi liệt kê nhiều dòng nên cần lâu hơn mặc định để đọc kịp
      showToast.error(summary.message, 9000);
    }
    // Cảnh báo không chặn (số dư mỏng) — toast riêng để không nhuộm đỏ kết quả thành công
    if (summary.warnings.length > 0) {
      showToast.info(summary.warnings.join('\n'), 9000);
    }
  };

  // Không tiền-kiểm số dư ở FE nữa: server là nơi duy nhất giữ luật (đọc số dư tươi
  // qua WS thay vì `balance` có thể đã cũ trong state, và đọc `sideConfigs` thật thay
  // vì parse lại config string). Server chặn khi không đủ một ván, còn số dư mỏng thì
  // vẫn cho chạy và trả `warning` — FE chỉ việc hiển thị lại.
  const handleStart = async () => {
    setStarting(true);
    try {
      const res = await sunwinStartLivestreamService(group._id);
      if (!res) {
        showToast.error('Start nhóm thất bại — không kết nối được tới máy chủ');
        return;
      }
      if (res.code !== 200) {
        showToast.error(res.message || 'Start nhóm thất bại');
        return;
      }
      for (const result of res.data || []) {
        if (result.success) setMemberStatus(result.accountId, 'RUNNING');
      }
      reportResult(res.data, 'Start nhóm');
    } finally {
      setStarting(false);
    }
  };

  const handleStop = async () => {
    const res = await sunwinStopLivestreamService(group._id);
    if (!res || res.code !== 200) {
      showToast.error(res?.message || 'Stop nhóm thất bại — không kết nối được tới máy chủ');
      return;
    }
    // Nhóm đang treo tiền ở phiên dở: server nhận lệnh nhưng CHƯA ngắt account
    // nào — nó đánh nốt phiên đó rồi tự dừng. Không được đổi status thành STOPPED
    // ở đây, nếu không thẻ nhóm hiện "đã dừng" trong khi nhóm vẫn đang chạy.
    if (res.deferred) {
      showToast.info(
        res.message || `Nhóm sẽ tự dừng sau khi phiên #${res.sessionId} kết thúc`,
        9000,
      );
      return;
    }
    for (const result of res.data || []) {
      if (result.success) setMemberStatus(result.accountId, 'STOPPED');
    }
    reportResult(res.data, 'Stop nhóm');
  };

  const handleRestart = async () => {
    const res = await sunwinRestartLivestreamService(group._id);
    if (!res || res.code !== 200) {
      showToast.error(res?.message || 'Restart nhóm thất bại — không kết nối được tới máy chủ');
      return;
    }
    for (const result of res.data || []) {
      setMemberStatus(result.accountId, result.success ? 'RUNNING' : 'STOPPED');
    }
    reportResult(res.data, 'Restart nhóm');
  };

  const handleLogin = async () => {
    try {
      setAuthAction('login');
      const res = await sunwinLoginGroupService(group._id);
      if (!res) {
        showToast.error('Đăng nhập nhóm thất bại — không kết nối được tới máy chủ');
        return;
      }
      if (Array.isArray(res.data)) {
        // Dùng login response (có đầy đủ balance/token) thay vì getGroupById
        for (const item of res.data) {
          if (item.success && item.data) {
            onUpdateAccount(item.accountId, () => ({ ...item.data, status: 'STOPPED' }));
          }
        }
      }
      // Backend luôn trả code 200 kèm mảng kết quả từng account, kể cả khi mọi
      // account đều lỗi — nên phải soi mảng đó chứ không thể tin mỗi status.
      reportResult(res.data, 'Đăng nhập nhóm');

      // Refetch group metadata (name, sideConfigs) không overwrite account state
      const freshGroup = await getGroupByIdService(group._id, { silent: true });
      if (freshGroup) onUpdateGroup(group._id, () => freshGroup);
    } catch (err: any) {
      console.error(err);
      showToast.error(err?.message || 'Đăng nhập nhóm thất bại');
    } finally {
      setAuthAction(null);
    }
  };

  const handleLogout = async () => {
    try {
      setAuthAction('logout');
      const res = await sunwinLogoutGroupService(group._id);
      if (!res || res.code !== 200) {
        showToast.error(res?.message || 'Đăng xuất nhóm thất bại — không kết nối được tới máy chủ');
        return;
      }
      reportResult(res.data, 'Đăng xuất nhóm');
      // onRefresh refetch cả accounts + groups từ BE — đầy đủ data
      await onRefresh();
    } catch (err: any) {
      console.error(err);
      showToast.error(err?.message || 'Đăng xuất nhóm thất bại');
    } finally {
      setAuthAction(null);
    }
  };

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="space-y-4 rounded-xl border border-[#252a38] bg-[#161920] p-5 shadow-sm"
      >
        {/* Group Header */}
        <div className="flex flex-col items-start justify-between gap-3 sm:flex-row sm:items-center">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <h3 className="text-lg font-bold text-[#e2e8f0]">{group.name}</h3>
              {gameType !== '?' && (
                <span
                  className={`rounded-md px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${badgeColor}`}
                >
                  {gameType}
                </span>
              )}
              <span className="rounded-md bg-[#252a38] px-2 py-0.5 text-[10px] font-bold text-[#94a3b8]">
                Chẵn: {getSideAmount(group.sideConfigs, AccountSide.CHAN).toLocaleString('vi-VN')}đ · Lẻ:{' '}
                {getSideAmount(group.sideConfigs, AccountSide.LE).toLocaleString('vi-VN')}đ
              </span>
              <button
                type="button"
                // Truyền members đã merge với accounts state: status/proxy ở đây mới
                // nhất, group fetch có thể còn cũ hơn một nhịp auto-refresh.
                onClick={() => onEditGroup?.({ ...group, members: memberAccountsMerged })}
                className="rounded p-1 text-[#64748b] transition-colors hover:bg-[#1e2235] hover:text-[#3b82f6]"
                title="Sửa nhóm — tên, game và cấu hình cược"
              >
                <Pencil size={13} />
              </button>
            </div>
            <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-xs font-medium">
              {memberAccountsMerged.length > 0 ? (
                memberAccountsMerged.map((m, i) => (
                  <span key={m.username} style={{ color: memberColors[i % memberColors.length] }}>
                    {m.username}
                  </span>
                ))
              ) : (
                <span className="text-[#64748b]">Chưa có thành viên</span>
              )}
            </div>
          </div>

          <div className="flex flex-col gap-1.5">
            {/* AUTH section */}
            <div className="grid grid-cols-[28px_1fr_1fr_1fr] gap-1">
              <span className="flex items-center text-[9px] font-bold uppercase tracking-wider text-[#64748b]">
                Auth
              </span>
              <button
                onClick={handleLogin}
                disabled={isAllLoggedIn || authAction !== null}
                className="inline-flex items-center justify-center gap-1 rounded-lg bg-[#3b82f6]/10 px-2 py-1.5 text-[10px] font-bold text-[#3b82f6] transition-all hover:bg-[#3b82f6]/20 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {authAction === 'login' ? (
                  <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-[#3b82f6] border-t-transparent" />
                ) : (
                  <LogIn className="h-3.5 w-3.5" />
                )}
                Login
              </button>
              <button
                onClick={handleLogout}
                disabled={isGroupDisabled || anyRunning || authAction !== null}
                className="inline-flex items-center justify-center gap-1 rounded-lg bg-[#64748b]/10 px-2 py-1.5 text-[10px] font-bold text-[#64748b] transition-all hover:bg-[#64748b]/20 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {authAction === 'logout' ? (
                  <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-[#64748b] border-t-transparent" />
                ) : (
                  <LogOut className="h-3.5 w-3.5" />
                )}
                Logout
              </button>
              {/* Lịch sử phiên vẫn giữ trên server sau khi Stop — chặn nút theo
                  `anyRunning` chỉ khoá mất đúng lúc khách cần xem lại nhóm vừa
                  dừng. Modal tự hiện "chưa có tài khoản nào đang chạy". */}
              <button
                onClick={() => setShowPhiênModal(true)}
                disabled={isGroupDisabled}
                className="inline-flex items-center justify-center gap-1 whitespace-nowrap rounded-lg bg-[#8b5cf6]/10 px-2 py-1.5 text-[10px] font-bold text-[#8b5cf6] transition-all hover:bg-[#8b5cf6]/20 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <Monitor className="h-3.5 w-3.5" />
                Show Phiên
              </button>
            </div>
            {/* CONTROL section */}
            <div className="grid grid-cols-[28px_1fr_1fr_1fr] gap-1">
              <span className="flex items-center text-[9px] font-bold uppercase tracking-wider text-[#64748b]">
                Ctrl
              </span>
              <button
                onClick={handleStart}
                disabled={isGroupDisabled || anyRunning || starting || !startEligibility.ok}
                title={!startEligibility.ok ? `Không thể start: ${startEligibility.reason}` : undefined}
                className="inline-flex items-center justify-center gap-1 rounded-lg bg-[#22c55e]/10 px-2 py-1.5 text-[10px] font-bold text-[#22c55e] transition-all hover:bg-[#22c55e]/20 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {starting ? (
                  <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-[#22c55e] border-t-transparent" />
                ) : (
                  <Play className="h-3.5 w-3.5" />
                )}
                {starting ? 'Đang kết nối...' : 'Start'}
              </button>
              <button
                onClick={handleStop}
                disabled={isGroupDisabled || !anyRunning}
                className="inline-flex items-center justify-center gap-1 rounded-lg bg-[#ef4444]/10 px-2 py-1.5 text-[10px] font-bold text-[#ef4444] transition-all hover:bg-[#ef4444]/20 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <Square className="h-3.5 w-3.5 shrink-0" />
                Stop
              </button>
              <button
                onClick={handleRestart}
                disabled={isGroupDisabled || !anyRunning}
                className="inline-flex items-center justify-center gap-1 rounded-lg bg-[#f59e0b]/10 px-2 py-1.5 text-[10px] font-bold text-[#f59e0b] transition-all hover:bg-[#f59e0b]/20 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <RotateCw className="h-3.5 w-3.5" />
                Restart
              </button>
            </div>
          </div>
        </div>

        {/* Shared session info — chỉ hiển thị khi có phiên active */}
        {sessionAccount && sharedSession.sessionId && (
          <SharedSessionPanel
            session={sharedSession}
            typegame={group.gameType || parseGameType(sessionAccount.config)}
          />
        )}

        {/* Group Members Grid */}
        {memberAccountsMerged.length > 0 ? (
          <div className="grid grid-cols-1 gap-4 pt-2 md:grid-cols-2">
            {memberAccountsMerged.map((acc, i) => (
              <AccountCard
                key={acc.username}
                acc={acc}
                isInGroup
                headerColor={memberColors[i % memberColors.length]}
                deleteDisabled={anyRunning}
                onDelete={onDeleteAccount}
                onGetBalance={onGetBalance}
                onGetUSDTQR={onGetUSDTQR}
                onGetHistory={onGetHistory}
                onGetBetLogs={onGetBetLogs}
                onWithdraw={onWithdraw}
              />
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-[#252a38] bg-transparent py-8 text-center">
            <Monitor className="mb-2 h-8 w-8 text-[#64748b]" />
            <span className="text-xs font-medium text-[#64748b]">
              Chưa có tài khoản trong nhóm.
            </span>
          </div>
        )}
      </motion.div>

      {/* Group Livestream Modal */}
      <GroupLivestreamModal
        isOpen={showPhiênModal}
        onClose={() => setShowPhiênModal(false)}
        groupName={group.name}
        groupId={group._id}
        members={memberAccountsMerged}
        gameType={group.gameType}
      />
    </>
  );
};
